import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ResolutionsPanel.css';

/**
 * Resolutions workspace, rendered inside the Corporate Records sub-agent of
 * the Business Auditor. Three columns:
 *   1. Left   — list of existing resolutions (drafts + signed) with filters
 *   2. Middle — preview / edit of the selected resolution
 *   3. Right  — template picker + form for a new resolution
 *
 * Backend: /api/opo/auditor/{resolution-templates,resolutions,resolutions/draft,
 *          resolutions/:id/sign,resolutions/:id/regenerate,resolutions/:id}
 */

const CATEGORY_LABELS = {
  finance: 'Finance',
  governance: 'Governance',
};

function fieldDefault(field) {
  if (field.default_today) return new Date().toISOString().slice(0, 10);
  if (field.default != null) return field.default;
  return '';
}

function useTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch('/api/opo/auditor/resolution-templates')
      .then((r) => r.json())
      .then((d) => setTemplates(d.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);
  return { templates, loading };
}

function ResolutionField({ field, value, onChange }) {
  const commonProps = {
    id: `res-field-${field.id}`,
    value: value ?? '',
    onChange: (e) => onChange(field.id, e.target.value),
    placeholder: field.placeholder || '',
    required: !!field.required,
  };
  if (field.type === 'textarea') {
    return <textarea {...commonProps} rows={field.rows || 4} />;
  }
  if (field.type === 'select') {
    return (
      <select {...commonProps}>
        <option value="">— select —</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  if (field.type === 'money' || field.type === 'number') {
    return <input type="number" step="0.01" {...commonProps} />;
  }
  if (field.type === 'date') {
    return <input type="date" {...commonProps} />;
  }
  return <input type="text" {...commonProps} />;
}

function ResolutionsList({ resolutions, activeId, onSelect, onDelete, reloadList }) {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(() => {
    if (filter === 'all') return resolutions;
    return resolutions.filter((r) => r.status === filter);
  }, [resolutions, filter]);

  return (
    <div className="rp-list">
      <div className="rp-list-header">
        <span className="rp-list-title">Existing resolutions</span>
        <button
          type="button"
          className="rp-btn-ghost"
          onClick={reloadList}
          title="Reload"
        >
          ↻
        </button>
      </div>
      <div className="rp-list-filters">
        {['all', 'draft', 'signed'].map((f) => (
          <button
            key={f}
            type="button"
            className={`rp-chip${filter === f ? ' is-active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && (
              <span className="rp-chip-count">
                {resolutions.filter((r) => r.status === f).length}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="rp-list-items">
        {filtered.length === 0 && (
          <p className="rp-empty">No resolutions {filter !== 'all' ? `in "${filter}"` : 'yet'}.</p>
        )}
        {filtered.map((r) => (
          <div
            key={r.id}
            className={`rp-list-item${activeId === r.id ? ' is-active' : ''}`}
            onClick={() => onSelect(r.id)}
          >
            <div className="rp-list-item-head">
              <span className="rp-list-item-no">#{r.resolution_number ?? '—'}</span>
              <span className={`rp-badge rp-badge--${r.status}`}>{r.status}</span>
            </div>
            <div className="rp-list-item-title">{r.title || r.template_slug}</div>
            <div className="rp-list-item-meta">
              {r.template_slug} · {r.effective_date || '—'}
            </div>
            {r.status === 'draft' && (
              <button
                type="button"
                className="rp-btn-link rp-list-item-del"
                onClick={(e) => { e.stopPropagation(); onDelete(r.id); }}
              >
                Delete draft
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResolutionForm({ templates, onDrafted }) {
  const [selectedSlug, setSelectedSlug] = useState('');
  const [params, setParams] = useState({});
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState(null);

  const template = templates.find((t) => t.slug === selectedSlug) || null;

  useEffect(() => {
    if (!template) { setParams({}); return; }
    const defaults = {};
    for (const f of template.fields || []) defaults[f.id] = fieldDefault(f);
    setParams(defaults);
  }, [template]);

  const onField = useCallback((id, val) => {
    setParams((prev) => ({ ...prev, [id]: val }));
  }, []);

  const canSubmit = template && (template.fields || []).every((f) => !f.required || (params[f.id] && String(params[f.id]).trim() !== ''));

  const draft = async () => {
    if (!canSubmit) return;
    setDrafting(true);
    setError(null);
    try {
      const res = await fetch('/api/opo/auditor/resolutions/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateSlug: selectedSlug, params }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Draft failed' }));
        throw new Error(err.error || 'Draft failed');
      }
      const data = await res.json();
      onDrafted(data);
      setSelectedSlug('');
      setParams({});
    } catch (e) {
      setError(e.message);
    } finally {
      setDrafting(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const t of templates) {
      const cat = t.category || 'governance';
      if (!g[cat]) g[cat] = [];
      g[cat].push(t);
    }
    return g;
  }, [templates]);

  return (
    <div className="rp-form">
      <div className="rp-form-header">
        <span className="rp-form-title">New resolution</span>
        <span className="rp-form-hint">Pick a template, fill the fields, generate an unsigned PDF.</span>
      </div>

      <div className="rp-templates">
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat} className="rp-templates-group">
            <div className="rp-templates-group-label">{CATEGORY_LABELS[cat] || cat}</div>
            <div className="rp-templates-grid">
              {list.map((t) => (
                <button
                  key={t.slug}
                  type="button"
                  className={`rp-template-tile${selectedSlug === t.slug ? ' is-active' : ''}`}
                  onClick={() => setSelectedSlug(t.slug)}
                >
                  <span className="rp-template-tile-label">{t.label}</span>
                  <span className="rp-template-tile-desc">{t.description}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {template && (
        <div className="rp-form-fields">
          <div className="rp-form-fields-header">
            <span>{template.label}</span>
            <span className="rp-form-fields-slug">{template.slug}</span>
          </div>
          <div className="rp-form-grid">
            {(template.fields || []).map((f) => (
              <label key={f.id} className={f.type === 'textarea' ? 'rp-form-field-wide' : 'rp-form-field'}>
                <span>{f.label}{f.required ? ' *' : ''}</span>
                <ResolutionField field={f} value={params[f.id]} onChange={onField} />
              </label>
            ))}
          </div>
          {error && <div className="rp-error">{error}</div>}
          <div className="rp-form-actions">
            <button type="button" className="rp-btn-secondary" onClick={() => setSelectedSlug('')}>
              Cancel
            </button>
            <button type="button" className="rp-btn-primary" onClick={draft} disabled={!canSubmit || drafting}>
              {drafting ? 'Generating…' : 'Generate unsigned PDF →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function parseJsonField(raw, fallback) {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return fallback; }
}

function stripHtml(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

function ResolutionPreview({ resolution, templates, onSigned, onUpdated }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editParams, setEditParams] = useState({});
  const [editTitle, setEditTitle] = useState('');
  const [editRecitals, setEditRecitals] = useState(['']);
  const [editResolved, setEditResolved] = useState(['']);
  const [editNotes, setEditNotes] = useState('');
  const [editEffective, setEditEffective] = useState('');

  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [resolution?.id]);

  if (!resolution) {
    return (
      <div className="rp-preview rp-preview--empty">
        <p>Select a resolution on the left or draft a new one.</p>
        <p className="rp-preview-hint">
          Every resolution is stored in <code>compliance/resolutions/</code> and cross-tagged to
          <strong> Corporate Records</strong>.
        </p>
      </div>
    );
  }

  const filePath = resolution.signed_path || resolution.unsigned_path;
  const viewUrl = filePath
    ? `/api/opo/auditor/compliance-document/view?file=${encodeURIComponent(filePath)}`
    : null;

  const template = (templates || []).find((t) => t.slug === resolution.template_slug) || null;
  const draft = parseJsonField(resolution.draft_json, null);
  const storedParams = parseJsonField(resolution.params_json, {});

  const startEdit = () => {
    setEditParams({ ...storedParams });
    setEditTitle(resolution.title || '');
    setEditEffective(resolution.effective_date || storedParams.effective_date || '');
    const recitals = (draft?.recitals || []).map(stripHtml);
    const resolved = (draft?.resolved || []).map(stripHtml);
    setEditRecitals(recitals.length ? recitals : ['']);
    setEditResolved(resolved.length ? resolved : ['']);
    setEditNotes(stripHtml(draft?.notes || ''));
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const onSignedFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/opo/auditor/resolutions/${resolution.id}/sign`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || 'Upload failed');
      }
      const data = await res.json();
      onSigned(data.resolution);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveEdit = async ({ rebuildFromTemplate = false } = {}) => {
    setSaving(true);
    setError(null);
    try {
      const body = rebuildFromTemplate
        ? {
            params: { ...editParams, effective_date: editEffective || editParams.effective_date },
            rebuildFromTemplate: true,
          }
        : {
            params: { ...editParams, effective_date: editEffective || editParams.effective_date },
            title: editTitle,
            effective_date: editEffective || undefined,
            recitals: editRecitals.filter((s) => String(s).trim()),
            resolved: editResolved.filter((s) => String(s).trim()),
            notes: editNotes,
          };
      const res = await fetch(`/api/opo/auditor/resolutions/${resolution.id}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Save failed' }));
        throw new Error(err.error || 'Save failed');
      }
      const data = await res.json();
      setEditing(false);
      if (typeof onUpdated === 'function') onUpdated(data.resolution);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rp-preview">
      <div className="rp-preview-header">
        <div>
          <div className="rp-preview-title">
            Resolution #{resolution.resolution_number ?? '—'} · {resolution.title}
          </div>
          <div className="rp-preview-meta">
            {resolution.template_slug} · effective {resolution.effective_date || '—'} ·
            <span className={`rp-badge rp-badge--${resolution.status}`}> {resolution.status}</span>
          </div>
        </div>
        <div className="rp-preview-actions">
          {resolution.status === 'draft' && !editing && (
            <button type="button" className="rp-btn-secondary" onClick={startEdit}>
              Edit
            </button>
          )}
          {viewUrl && !editing && (
            <a
              className="rp-btn-secondary"
              href={viewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open PDF →
            </a>
          )}
          {resolution.status === 'draft' && !editing && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={onSignedFileChange}
              />
              <button
                type="button"
                className="rp-btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading…' : 'Upload signed PDF →'}
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                type="button"
                className="rp-btn-primary"
                onClick={() => saveEdit({ rebuildFromTemplate: false })}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save & regenerate PDF'}
              </button>
              {template?.fields?.length > 0 && (
                <button
                  type="button"
                  className="rp-btn-secondary"
                  onClick={() => saveEdit({ rebuildFromTemplate: true })}
                  disabled={saving}
                  title="Re-apply template wording from the fields below"
                >
                  {saving ? 'Saving…' : 'Rebuild from fields'}
                </button>
              )}
              <button type="button" className="rp-btn-secondary" onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="rp-error">{error}</div>}

      {editing ? (
        <div className="rp-edit">
          <div className="rp-edit-section">
            <label className="rp-form-field-wide">
              <span>Title</span>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </label>
            <label className="rp-form-field">
              <span>Effective date</span>
              <input
                type="date"
                value={editEffective || ''}
                onChange={(e) => setEditEffective(e.target.value)}
              />
            </label>
          </div>

          {template?.fields?.length > 0 && (
            <div className="rp-edit-section">
              <div className="rp-preview-section-title">Template fields</div>
              <p className="rp-edit-hint">
                Change amounts / names here, then use <strong>Rebuild from fields</strong> to refresh
                the wording — or edit the clauses below and <strong>Save &amp; regenerate PDF</strong>.
              </p>
              <div className="rp-form-grid">
                {template.fields.map((f) => (
                  <label
                    key={f.id}
                    className={f.type === 'textarea' ? 'rp-form-field-wide' : 'rp-form-field'}
                  >
                    <span>{f.label}{f.required ? ' *' : ''}</span>
                    <ResolutionField
                      field={f}
                      value={editParams[f.id]}
                      onChange={(id, val) => setEditParams((prev) => ({ ...prev, [id]: val }))}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="rp-edit-section">
            <div className="rp-preview-section-title">Recitals</div>
            {editRecitals.map((line, i) => (
              <div key={`rec-${i}`} className="rp-edit-line">
                <textarea
                  rows={3}
                  value={line}
                  onChange={(e) => {
                    const next = [...editRecitals];
                    next[i] = e.target.value;
                    setEditRecitals(next);
                  }}
                />
                <button
                  type="button"
                  className="rp-btn-link"
                  onClick={() => setEditRecitals(editRecitals.filter((_, j) => j !== i))}
                  disabled={editRecitals.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="rp-btn-ghost"
              onClick={() => setEditRecitals([...editRecitals, ''])}
            >
              + Add recital
            </button>
          </div>

          <div className="rp-edit-section">
            <div className="rp-preview-section-title">Resolved</div>
            {editResolved.map((line, i) => (
              <div key={`res-${i}`} className="rp-edit-line">
                <textarea
                  rows={3}
                  value={line}
                  onChange={(e) => {
                    const next = [...editResolved];
                    next[i] = e.target.value;
                    setEditResolved(next);
                  }}
                />
                <button
                  type="button"
                  className="rp-btn-link"
                  onClick={() => setEditResolved(editResolved.filter((_, j) => j !== i))}
                  disabled={editResolved.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="rp-btn-ghost"
              onClick={() => setEditResolved([...editResolved, ''])}
            >
              + Add resolved clause
            </button>
          </div>

          <div className="rp-edit-section">
            <label className="rp-form-field-wide">
              <span>Notes</span>
              <textarea
                rows={3}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </label>
          </div>
        </div>
      ) : (
        <>
          {draft && (
            <div className="rp-preview-body">
              <div className="rp-preview-section">
                <div className="rp-preview-section-title">Recitals</div>
                {draft.recitals?.map((line, i) => (
                  <p key={i} className="rp-clause rp-clause--recital" dangerouslySetInnerHTML={{ __html: line }} />
                ))}
              </div>
              <div className="rp-preview-section">
                <div className="rp-preview-section-title">Resolved</div>
                <ol className="rp-clauses">
                  {draft.resolved?.map((line, i) => (
                    <li key={i} className="rp-clause" dangerouslySetInnerHTML={{ __html: line }} />
                  ))}
                </ol>
              </div>
              {draft.notes && (
                <div className="rp-preview-section">
                  <div className="rp-preview-section-title">Notes</div>
                  <p className="rp-clause rp-clause--note" dangerouslySetInnerHTML={{ __html: draft.notes }} />
                </div>
              )}
            </div>
          )}

          <div className="rp-preview-footer">
            <p className="rp-preview-hint">
              The unsigned PDF is at <code>{resolution.unsigned_path || '—'}</code>. Use <strong>Edit</strong> to
              change wording or amounts, save to regenerate the PDF, then print, sign both director lines,
              and upload the signed scan.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

const ResolutionsPanel = ({ embedded = false }) => {
  const { templates, loading: templatesLoading } = useTemplates();
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const reloadList = useCallback(() => {
    setLoading(true);
    fetch('/api/opo/auditor/resolutions')
      .then((r) => r.json())
      .then((d) => setResolutions(d.resolutions || []))
      .catch(() => setResolutions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reloadList(); }, [reloadList]);

  const active = resolutions.find((r) => r.id === activeId) || null;

  const onDrafted = (data) => {
    reloadList();
    if (data.resolution?.id) setActiveId(data.resolution.id);
  };

  const onSigned = (updated) => {
    reloadList();
    if (updated?.id) setActiveId(updated.id);
  };

  const onUpdated = (updated) => {
    reloadList();
    if (updated?.id) setActiveId(updated.id);
  };

  const onDelete = (id) => {
    if (!window.confirm('Delete this draft resolution?')) return;
    fetch(`/api/opo/auditor/resolutions/${id}`, { method: 'DELETE' })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Delete failed'))))
      .then(() => { if (activeId === id) setActiveId(null); reloadList(); })
      .catch((err) => alert(err.message));
  };

  return (
    <div className={`rp-panel${embedded ? ' rp-panel--embedded' : ''}`}>
      {!embedded && (
        <div className="rp-panel-header">
          <div>
            <h2 className="rp-panel-title">Resolutions</h2>
            <p className="rp-panel-subtitle">
              Board and shareholder resolutions for OptiPrime AI Inc. Templates cover loan repayment,
              share issuance, banking signatories, dividends, capital purchases, and free-form.
            </p>
          </div>
        </div>
      )}

      <div className="rp-columns rp-columns--view-center">
        <ResolutionsList
          resolutions={resolutions}
          activeId={activeId}
          onSelect={setActiveId}
          onDelete={onDelete}
          reloadList={reloadList}
        />
        <ResolutionPreview
          resolution={active}
          templates={templates}
          onSigned={onSigned}
          onUpdated={onUpdated}
        />
        <div className="rp-center">
          <ResolutionForm templates={templates} onDrafted={onDrafted} />
          {templatesLoading && <p className="rp-empty">Loading templates…</p>}
          {loading && !resolutions.length && <p className="rp-empty">Loading resolutions…</p>}
        </div>
      </div>
    </div>
  );
};

export default ResolutionsPanel;
